import type { QueryClient } from '@tanstack/react-query';
import { cargoProblemsKeys } from '../features/cargo-problems/index.js';
import { NOTIFICATIONS_LIST_QK } from '../features/notifications/index.js';
import { requestsKeys } from '../features/requests/queries.js';
import { russiaRequestsKeys } from '../features/russia-requests/queries.js';
import { useRequestsStore } from '../features/requests/store.js';
import { invalidateLeadCaches } from './leadRealtimeHandlers.js';

/**
 * @aiPurpose reconnect / browser-lifecycle catch-up cache reset for every
 *   realtime-backed surface. Triggered by `RealtimeBootstrap` whenever a
 *   missed-event window may have opened (Socket.IO `reconnect`, a
 *   recovery-driven `connect`, or a Page Lifecycle resume).
 * @aiInvariant invalidation scope is intentionally domain-specific —
 *   only realtime-backed slices (leads/engagements, china/russia requests
 *   and their chat, plus the notifications list) are touched. Catch-up must
 *   NOT enable `refetchOnWindowFocus` globally; users/audit/etc. are pull-by-
 *   mutation and do not need it. See `docs/ops/realtime.md`.
 */
export function invalidateRequestCaches(queryClient: QueryClient): void {
  invalidateLeadCaches(queryClient);
  void queryClient.invalidateQueries({ queryKey: requestsKeys.all });
  // INVARIANT (#570): catch-up MUST refresh the unread-summary slice too,
  //   otherwise a backgrounded tab returning from bfcache shows stale
  //   badge counts until the next mutation.
  void queryClient.invalidateQueries({ queryKey: requestsKeys.chatUnreadSummaryPrefix() });
  // INVARIANT (#706 Slice B): russia-requests is a sibling domain on the
  //   same socket. A reconnect catch-up therefore MUST refresh both its
  //   list prefix (`['russia-requests']`) and its detail prefix
  //   (`['russia-request']`) so a backgrounded /russia-rates or open
  //   /russia-rates/:id drawer does not silently show stale data after
  //   the gateway re-establishes. The detail prefix is invalidated in
  //   bulk because no russia-side `selectedId` mirror exists yet —
  //   unlike the China path above, we cannot target the specific id.
  void queryClient.invalidateQueries({ queryKey: russiaRequestsKeys.all });
  void queryClient.invalidateQueries({ queryKey: russiaRequestsKeys.detailPrefix() });
  // INVARIANT (#1067): RF chat slice must refresh on reconnect catch-up
  //   so backgrounded /russia-rates drawers do not show stale chat or
  //   stale unread-summary counts after the socket re-establishes.
  void queryClient.invalidateQueries({ queryKey: russiaRequestsKeys.chatPrefix() });
  void queryClient.invalidateQueries({ queryKey: russiaRequestsKeys.chatUnreadSummaryPrefix() });
  // WHY (#1072): the notifications list backs the bell + row/tab badges;
  //   bfcache restore / focus catch-up must reconcile it or stale unread
  //   counters could trip a row dot for an already-viewed aggregate.
  void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_QK });
  // WHY (#2868): a missed-event window can hide a whole cargo-problem
  //   workspace turn — a status move, a message, the attachments it bound and
  //   the audit row for a deleted one — plus the attention signal they raise.
  //   Each family is invalidated by prefix because the workspace caches per
  //   problem id (and the library also per filter+search) and no mirror of the
  //   open problem exists here; only the queries a mounted pane actually
  //   observes refetch. The whole cargo-problem root stays untouched — it would
  //   also refetch every list/summary/filter query on each reconnect for no new
  //   truth.
  void queryClient.invalidateQueries({ queryKey: cargoProblemsKeys.attention() });
  for (const family of [
    'detail',
    'workspace-capabilities',
    'workspace-messages',
    'workspace-materials',
    'activity',
  ]) {
    void queryClient.invalidateQueries({ queryKey: [...cargoProblemsKeys.all, family] });
  }
  const selectedId = useRequestsStore.getState().selectedId;
  if (selectedId) {
    void queryClient.invalidateQueries({ queryKey: requestsKeys.detail(selectedId) });
    void queryClient.invalidateQueries({ queryKey: requestsKeys.chat(selectedId) });
    void queryClient.invalidateQueries({ queryKey: requestsKeys.snapshots(selectedId) });
  }
}
